import { useEffect, useRef } from "react";
import { Avatar, Box, CircularProgress, IconButton, Tooltip, Typography, keyframes } from "@mui/material";
import { colors } from "../../config";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MicIcon from "@mui/icons-material/Mic";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import useAuthentication from "../../hooks/useAuthentication";

export type EmrChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
};

export type VoiceChatListeningState = {
  listening: boolean;
  previewText: string;
};

type Props = {
  messages: EmrChatMessage[];
  isLoading?: boolean;
  voiceListening?: VoiceChatListeningState;
  speechOutputSupported?: boolean;
  speakingMessageId?: string | null;
  onSpeakMessage?: (messageId: string, text: string) => void;
};

const listeningPulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.55; transform: scale(0.92); }
`;

const avatarSx = {
  width: 28,
  height: 28,
  flexShrink: 0,
  boxShadow: "0 6px 16px rgba(33,150,243,0.28)",
  border: "2px solid #fff",
  fontSize: "0.75rem",
  fontWeight: 700,
} as const;

function AssistantAvatar({
  onSpeak,
  speaking,
  disabled,
}: {
  onSpeak?: () => void;
  speaking?: boolean;
  disabled?: boolean;
}) {
  const avatar = (
    <Box
      sx={{
        ...avatarSx,
        borderRadius: "50%",
        background: `linear-gradient(135deg, ${colors.primaryMain} 0%, ${colors.primaryDark} 100%)`,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...(speaking
          ? { animation: `${listeningPulse} 1.4s ease-in-out infinite` }
          : {}),
      }}
    >
      {speaking ? (
        <VolumeUpIcon sx={{ fontSize: 14 }} />
      ) : (
        <AutoAwesomeIcon sx={{ fontSize: 14 }} />
      )}
    </Box>
  );

  if (!onSpeak) return avatar;

  return (
    <Tooltip title={speaking ? "Playing…" : "Read aloud"}>
      <span>
        <IconButton
          size="small"
          aria-label={speaking ? "Playing message" : "Read message aloud"}
          onClick={onSpeak}
          disabled={disabled || speaking}
          sx={{ p: 0 }}
        >
          {avatar}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function UserAvatar({ photo, name }: { photo?: string | null; name?: string }) {
  const initial = name?.trim().charAt(0)?.toUpperCase() || "U";

  return (
    <Avatar src={photo || undefined} alt={name || "User"} sx={{ ...avatarSx, bgcolor: colors.primaryMain }}>
      {!photo ? initial : null}
    </Avatar>
  );
}

export default function EmrAiChatThread({
  messages,
  isLoading,
  voiceListening,
  speechOutputSupported,
  speakingMessageId,
  onSpeakMessage,
}: Props) {
  const { user } = useAuthentication();
  const userPhoto = user?.photo || null;
  const userName = user?.emp_name || user?.username || "User";
  const bottomRef = useRef<HTMLDivElement>(null);
  const showVoiceListening = Boolean(voiceListening?.listening);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading, showVoiceListening, voiceListening?.previewText]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        minHeight: "min-content",
        width: "100%",
      }}
    >
      {messages.map((msg) => {
        const isUser = msg.role === "user";
        return (
          <Box
            key={msg.id}
            sx={{
              display: "flex",
              justifyContent: isUser ? "flex-end" : "flex-start",
              alignItems: "flex-end",
              gap: 0.75,
            }}
          >
            {!isUser ? (
              <AssistantAvatar
                onSpeak={
                  speechOutputSupported && onSpeakMessage
                    ? () => onSpeakMessage(msg.id, msg.content)
                    : undefined
                }
                speaking={speakingMessageId === msg.id}
                disabled={Boolean(speakingMessageId && speakingMessageId !== msg.id)}
              />
            ) : null}
            <Box
              sx={{
                maxWidth: "88%",
                px: 1.75,
                py: 1.15,
                borderRadius: isUser ? "18px 18px 6px 18px" : "18px 18px 18px 6px",
                bgcolor: isUser ? "transparent" : "rgba(255,255,255,0.95)",
                color: isUser ? "#fff" : "text.primary",
                background: isUser
                  ? `linear-gradient(135deg, ${colors.primaryMain} 0%, ${colors.primaryDark} 100%)`
                  : "rgba(255,255,255,0.95)",
                border: isUser ? "none" : "1px solid",
                borderColor: "rgba(148,163,184,0.28)",
                boxShadow: isUser
                  ? "0 10px 24px rgba(33,150,243,0.28)"
                  : "0 8px 20px rgba(15,23,42,0.05)",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.8125rem",
                  lineHeight: 1.55,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {msg.content}
              </Typography>
            </Box>
            {isUser ? <UserAvatar photo={userPhoto} name={userName} /> : null}
          </Box>
        );
      })}
      {showVoiceListening ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-end",
            gap: 0.75,
          }}
        >
          <Box
            sx={{
              maxWidth: "88%",
              px: 1.75,
              py: 1.15,
              borderRadius: "18px 18px 6px 18px",
              color: "#fff",
              background: `linear-gradient(135deg, ${colors.primaryMain} 0%, ${colors.primaryDark} 100%)`,
              border: "1px dashed",
              borderColor: "rgba(255,255,255,0.45)",
              boxShadow: "0 10px 24px rgba(33,150,243,0.28)",
              opacity: 0.95,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: voiceListening?.previewText ? 0.5 : 0 }}>
              <MicIcon
                sx={{
                  fontSize: 16,
                  animation: `${listeningPulse} 1.4s ease-in-out infinite`,
                }}
              />
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, letterSpacing: 0.3, textTransform: "uppercase" }}
              >
                Listening…
              </Typography>
            </Box>
            {voiceListening?.previewText ? (
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.8125rem",
                  lineHeight: 1.55,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontStyle: "italic",
                }}
              >
                {voiceListening.previewText}
              </Typography>
            ) : null}
          </Box>
          <UserAvatar photo={userPhoto} name={userName} />
        </Box>
      ) : null}
      {isLoading ? (
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.75 }}>
          <AssistantAvatar />
          <Box
            sx={{
              px: 1.75,
              py: 1.15,
              borderRadius: "18px 18px 18px 6px",
              bgcolor: "rgba(255,255,255,0.95)",
              border: "1px solid",
              borderColor: "rgba(148,163,184,0.28)",
              boxShadow: "0 8px 20px rgba(15,23,42,0.05)",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">
              Thinking…
            </Typography>
          </Box>
        </Box>
      ) : null}
      <div ref={bottomRef} />
    </Box>
  );
}
